const DEFAULT_LOCALE = "es-ES";

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

// A calendar date ("1994-02-17") has no zone: `new Date()` reads it as UTC midnight, the previous day west of UTC.
export function parseDateInput(dateInput: string | Date): Date {
  if (typeof dateInput !== "string") return dateInput;
  const ymd = CALENDAR_DATE.exec(dateInput);
  return ymd ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])) : new Date(dateInput);
}

/**
 * Formats a date (and optionally time) for display: day (number), month (name), year (number), and time when
 * present. Reusable across the app (confirmations, history, etc.).
 *
 * @param dateInput - ISO date/datetime string (e.g. "2026-02-04" or "2026-02-04T14:30:00Z") or Date
 * @param options - Locale (default "es-ES"), includeTime (default: true when input has time)
 * @returns E.g. "4 febrero 2026" or "4 febrero 2026, 14:30"
 */
export function formatDateTime(
  dateInput: string | Date | null | undefined,
  options?: { locale?: string; includeTime?: boolean },
): string {
  if (dateInput == null) return "";
  const date = parseDateInput(dateInput);
  if (Number.isNaN(date.getTime())) return "";

  const locale = options?.locale ?? DEFAULT_LOCALE;
  const datePart = date.toLocaleDateString(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const hasTime =
    options?.includeTime !== undefined
      ? options.includeTime
      : typeof dateInput === "string" && dateInput.includes("T");
  if (!hasTime) return datePart;

  const timePart = date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${datePart}, ${timePart}`;
}

/** Unit thresholds for `formatTimeAgo`, from the smallest unit up. */
const TIME_AGO_UNITS: {
  limitSeconds: number;
  secondsPerUnit: number;
  unit: Intl.RelativeTimeFormatUnit;
}[] = [
  { limitSeconds: 60, secondsPerUnit: 1, unit: "second" },
  { limitSeconds: 3600, secondsPerUnit: 60, unit: "minute" },
  { limitSeconds: 86400, secondsPerUnit: 3600, unit: "hour" },
  { limitSeconds: 2592000, secondsPerUnit: 86400, unit: "day" },
];

/**
 * Locale-aware "time ago" label — "hace 12 minutos", "12 minutes ago", "há 12 minutos" — built with
 * `Intl.RelativeTimeFormat`, so the three app locales are covered without a translation key per unit.
 *
 * Pass the active language (`useTranslations().activeLanguage`) as the locale. Anything older than 30 days
 * falls back to the absolute date, which reads better than "hace 14 meses" for a stale timestamp.
 *
 * @param dateInput - ISO datetime string or Date
 * @returns E.g. "ahora", "hace 12 minutos", "ayer", or "4 febrero 2026, 14:30" past the 30-day cutoff
 */
export function formatTimeAgo(
  dateInput: string | Date | null | undefined,
  options?: { locale?: string },
): string {
  if (dateInput == null) return "";
  const date = parseDateInput(dateInput);
  if (Number.isNaN(date.getTime())) return "";

  const locale = options?.locale ?? DEFAULT_LOCALE;
  // Clamped at zero: a server clock a few seconds ahead of the browser would otherwise render the lock as
  // "in 5 seconds".
  const elapsedSeconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));

  const match = TIME_AGO_UNITS.find(({ limitSeconds }) => elapsedSeconds < limitSeconds);
  if (!match) return formatDateTime(dateInput, { locale, includeTime: true });

  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  return formatter.format(-Math.floor(elapsedSeconds / match.secondsPerUnit), match.unit);
}
